<template>
    <m-popper direction="bottom" :show="focus">
        <m-input :active="focus" @click="setFocus()" slot="target">
            <input class="w-full p-2 outline-none border border-gray-400 rounded-md shadow-md pb-2" v-model="inputText" type="text"
                   @focus="updateFocus(true)" @blur="updateFocus(false)" ref="input" @keydown.enter="hide()"/>
            <div class="sprite-icons-calendar" slot="icon"></div>
        </m-input>
        <div class="shadow w-56 bg-white overflow-auto" @mousedown.prevent="">
            <div class="bg-primary-600 text-white">
                <div class="pl-1 pt-1">{{ model | date('%Y') }}</div>
                <div class="text-sm pl-1 pb-1">{{ model | date('%D %M %j') }}</div>
            </div>
            <div class="relative text-center cursor-pointer pl-6 pr-6 no-wrap">
                <div class="p-1 inline-block" @click="mode = 1">
                    <div class="inline-block align-middle">{{ headingMonth }}</div>
                    <div class="sprite-icons-select inline-block align-middle"></div>
                </div>
                <div class="p-1 inline-block" @click="mode = 2">
                    <div class="inline-block align-middle">&nbsp;{{ year }}</div>
                    <div class="sprite-icons-select inline-block align-middle"></div>
                </div>
                <div class="cursor-pointer mt-2 absolute left-0 top-0 sprite-icons-left" v-show="mode == 0"
                     @click="prevMonth"></div>
                <div class="cursor-pointer mt-2 absolute right-0 top-0 sprite-icons-right" v-show="mode == 0"
                     @click="nextMonth"></div>
            </div>
            <template v-if="mode == 0">
                <table class="w-full">
                    <th class="text-sm text-gray-500 w-10 h-10" v-for="d in dayHeading">{{ d }}</th>
                    <tr v-for="week in getTable">
                        <td class="cursor-pointer text-center w-8 h-8" v-for="day in week" :class="day._class"
                            @mousedown="updateFromTable(day.date)">{{ day.date.getDate() }}
                        </td>
                    </tr>
                </table>
            </template>
            <template v-else-if="mode == 1">
                <table class="w-full">
                    <tr class="text-center" v-for="row in monthRange()">
                        <td class="cursor-pointer p-1 text-center" v-for="col in row" @click="selectMonth(col.month)"
                            :class="{'color-primary-bg': col.month == month}">{{ col.text }}
                        </td>
                    </tr>
                </table>
            </template>
            <template v-else>
                <table class="w-full">
                    <tr v-for="row in yearRange()">
                        <td class="cursor-pointer text-sm p-1 text-center" v-for="col in row" @click="selectYear(col)"
                            :class="{'color-primary-bg': col == year}">{{ col }}
                        </td>
                    </tr>
                </table>
            </template>
        </div>
    </m-popper>
</template>
<script>
import {getWindowScroll, getAbsolute, getElementHeight} from '@/js/dom.js'
import {dateFormat, dateCompare, dayHeadingSingle, monthHeadingFull, monthHeadingShort} from '@/js/date'

// Restricts input for the given textbox to the given inputFilter.
function setInputFilter(textbox, inputFilter, update) {
    [
        "input",
        "keydown",
        "keyup",
        "mousedown",
        "mouseup",
        "select",
        "contextmenu",
        "drop"
    ].forEach(function (event) {
        textbox.addEventListener(event, function () {
            let value = inputFilter(this.value)
            if (value) {
                this.oldValue = this.value;
                this.oldSelectionStart = this.selectionStart;
                this.oldSelectionEnd = this.selectionEnd;
                if (typeof value != 'boolean') {
                    update(value)
                }
            } else if (this.hasOwnProperty("oldValue")) {
                this.value = this.oldValue;
                update(this.value)
                this.setSelectionRange(
                    this.oldSelectionStart,
                    this.oldSelectionEnd
                );
            }
        });
    });
}

function getHeadings(type, start) {
    let count = DateHelper.headings[type].length
    let ret = []
    for (let i = 0; i < count; i++) {
        ret.push(DateHelper.headings[type][(i + start) % count])
    }
    return ret
}

function getDayClass(date, current, selMonth) {
    if (dateCompare(date, current)) {
        return 'rounded-full text-white bg-primary-600'
    }
    if (date.getMonth() == selMonth) {
        return 'text-black'
    }
    return 'text-gray-400'
}

// Take a whole month and divide it 5 weeks.
function createRows(startDay, month, year, current) {
    // Month is zero based
    let firstDay = new Date(year, month, 1).getDay()
    let weekfactor = startDay - firstDay
    if (weekfactor > 0) {
        weekfactor -= 7
    }

    let weeks = []
    for (let wn = 0; wn < 6; wn++) {
        let week = []

        for (let wd = 0; wd < 7; wd++) {
            let day = (wn * 7) + wd + weekfactor
            let entry_date = new Date(year, month, day + 1)  // day should not be zero based
            week.push({
                date: entry_date,
                current: entry_date.getMonth() == month,
                _class: getDayClass(entry_date, current, month)
            })
        }
        weeks.push(week)
    }
    return weeks
}

function isDigit(v) {
    return /^\d*$/.test(v)
}

function parseDate(val) {
    // return true if the input is partially valid
    // return false if input is invalid - need to reset to previous valid state
    // return datetime if whole input is valid and needs to be updated 

    // 25-04-1990
    var parts = val.split('-')
    var d, m, y = 0
    var ret = true
    // var digits = parts.map(m => parseInt(m))
    if (parts.length >= 1 && parts[0].length > 0) {
        // partial valid input or not valid
        d = parseInt(parts[0])
        ret = d <= 31
    }
    if (parts.length >= 2 && parts[1].length > 0) {
        m = parseInt(parts[1])
        ret = ret && parts[1] <= 12
    }
    if (parts.length >= 3 && parts[2].length > 0) {
        y = parseInt(parts[2])
        ret = new Date(y, m - 1, d)
    }

    return ret
}


// Convert date to string for comparison
function dateStr(date) {
    return date ? date.toString() : ''
}

export default {
    name: 'datepicker',
    props: {
        value: Date,
        displayformat: {
            type: String,
            default: '%j %F %Y'
        },
        editformat: {
            type: String,
            default: '%d-%m-%Y'
        }
    },
    watch: {
        value: {
            handler: 'updateText',
            immediate: true
        }
    },
    created() {
        if (typeof (this.value) == 'string') {
            this.$emit('input', new Date(this.value))
        }
    },
    data() {
        var now = this.model || new Date()
        return {
            mode: 0,
            focus: false,
            inputText: '',
            // Selected year and month
            year: now.getFullYear(),
            month: now.getMonth()
        }
    },
    methods: {
        selectYear(year) {
            this.year = year
            this.mode = 0
            this.$emit('input', new Date(this.year, this.month, this.model.getDate()))
        },
        selectMonth(month) {
            this.month = month
            this.mode = 0
            this.$emit('input', new Date(this.year, this.month, this.model.getDate()))
        },
        monthRange() {
            return [
                monthHeadingShort.slice(0, 3).map((m, i) => ({text: m, month: i})),
                monthHeadingShort.slice(3, 6).map((m, i) => ({text: m, month: i + 3})),
                monthHeadingShort.slice(6, 9).map((m, i) => ({text: m, month: i + 6})),
                monthHeadingShort.slice(9, 12).map((m, i) => ({text: m, month: i + 9})),
            ]
        },
        yearRange() {
            var y = new Date().getFullYear()
            // Create 25 rows of 5 cols
            const rows = 20
            const cols = 5
            return Array(20).fill(0).map((_, row) => Array(5).fill(0).map((_, col) => y - (row * 5) - col))
        },
        updateFocus(focus) {
            this.focus = focus
            this.updateText()
        },
        updateText() {
            if (this.value && this.value instanceof Date) {
                this.inputText = dateFormat(this.model, this.focus ? this.editformat : this.displayformat)
            } else {
                this.inputText = ''
            }
        },
        hide() {
            // First process change then hide the input
            this.$nextTick(() => {
                this.$refs.input.blur()
            })
        },
        setFocus(state) {
            this.$refs.input.focus();
            this.updateFocus(true)
        },
        updateFromTable(val) {
            this.$emit('input', val)
            this.hide()
        },
        prevMonth() {
            if (--this.month < 0) {
                this.year--
                this.month = 11
            }
        },
        nextMonth() {
            if (++this.month >= 12) {
                this.year++
                this.month = 0
            }
        }
    },
    computed: {
        model() {
            if (this.value && this.value.constructor == Date) {
                return this.value
            }
            return new Date()
        },
        getTable() {
            return createRows(0, this.month, this.year, this.model || new Date())
        },
        dayHeading() {
            return dayHeadingSingle
        },
        headingMonth() {
            return monthHeadingShort[this.month]
        }

    },
    mounted() {
        setInputFilter(this.$refs.input, parseDate, val => {
            // test if object (Date) and not string
            if (typeof (val) == "object") {
                // only emit if changed
                if (dateStr(this.value) != dateStr(val)) {
                    this.$emit('input', val)
                }
                // update selected month and year
                
                //console.log("new this.value : " + this.value)
            }
        })
    }
}
</script>
